import { Grid } from '../../core/Grid.js';
import {
    Position,
    positionKey,
    parsePositionKey,
} from '../../core/GridGeometry.js';
import { KnightGame, parseSegmentKey } from './KnightGame.js';
import { VortexGateManager } from '../VortexGateManager.js';
import { IVortexGame } from '../IVortexGame.js';

export class KnightVortex extends KnightGame implements IVortexGame {
    private readonly gm = new VortexGateManager();
    // Directional: entryGateKey -> exitPairedKey (tracks teleports in the current path)
    private teleportedPairs: Map<string, string> = new Map();

    constructor(grid: Grid) {
        super(grid);
    }

    // -- Vortex Data -------------------------------------------------------

    setVortexData(gatePairs: Array<[Position, Position]>, allGates: Set<string>): void {
        this.gm.setVortexData(gatePairs, allGates, () => {
            // Remove teleportedPairs for invalidated gate pairs
            const teleportToRemove: string[] = [];
            for (const [entryKey, exitKey] of this.teleportedPairs) {
                if (!this.gm.raw.has(entryKey) || this.gm.raw.get(entryKey) !== exitKey) {
                    teleportToRemove.push(entryKey);
                }
            }
            for (const key of teleportToRemove) {
                this.teleportedPairs.delete(key);
            }
        });
    }

    // -- Gate Queries ------------------------------------------------------

    isGateCell(pos: Position): boolean {
        return this.gm.isGateCell(pos);
    }

    isActiveGate(pos: Position): boolean {
        return this.gm.isActiveGate(pos);
    }

    canActivateGate(pos: Position): boolean {
        return this.gm.canActivateGate(pos, (p, pairedPos) => {
            const key = positionKey(p);
            const pairedKey = positionKey(pairedPos);
            // Knight-specific: neither cell can be in the path
            if (this.visitedCells.has(key)) return false;
            if (this.visitedCells.has(pairedKey)) return false;
            if (this.grid.hasDigitAt(p)) return false;
            if (this.grid.hasDigitAt(pairedPos)) return false;
            return true;
        });
    }

    toggleVortexGate(pos: Position): boolean {
        const key = positionKey(pos);

        if (this.gm.raw.has(key)) {
            const pairedKey = this.gm.raw.get(key)!;
            this.gm.raw.delete(key);
            this.gm.raw.delete(pairedKey);
            // Remove teleport link if one exists
            if (this.teleportedPairs.has(key)) {
                this.teleportedPairs.delete(key);
            } else if (this.teleportedPairs.has(pairedKey)) {
                this.teleportedPairs.delete(pairedKey);
            }
            this.rebuildState();
            return true;
        }

        if (this.canActivateGate(pos)) {
            const pair = this.gm.findPair(key);
            if (pair) {
                this.gm.raw.set(key, pair.pairedKey);
                this.gm.raw.set(pair.pairedKey, key);
                return true;
            }
        }
        return false;
    }

    getActiveGatePairs(): Map<string, string> {
        return this.gm.getActiveGatePairs();
    }

    getVortexGatePairs(): Array<[Position, Position]> {
        return this.gm.getVortexGatePairs();
    }

    getAllGatePositions(): Set<string> {
        return this.gm.getAllGatePositions();
    }

    // -- Teleport Link Access ----------------------------------------------

    getTeleportedPairs(): Map<string, string> {
        return new Map(this.teleportedPairs);
    }

    // -- Adjacency with Teleports ------------------------------------------

    protected override buildAdjacencyMap(): Map<string, string[]> {
        const adjacency = super.buildAdjacencyMap();

        for (const [entryKey, exitKey] of this.teleportedPairs) {
            if (!adjacency.has(entryKey)) adjacency.set(entryKey, []);
            if (!adjacency.has(exitKey)) adjacency.set(exitKey, []);
            adjacency.get(entryKey)!.push(exitKey);
            adjacency.get(exitKey)!.push(entryKey);
        }

        return adjacency;
    }

    // -- Rebuild State with Teleport Cleanup --------------------------------

    protected override rebuildState(): void {
        super.rebuildState();

        // Remove orphaned teleport links (entry gate no longer in visitedCells from segments)
        const toRemove: string[] = [];
        for (const [entryKey] of this.teleportedPairs) {
            if (!this.visitedCells.has(entryKey)) {
                toRemove.push(entryKey);
            }
        }
        for (const key of toRemove) {
            this.teleportedPairs.delete(key);
        }

        // Add teleport-linked cells to visitedCells and digitsReached
        for (const [entryKey, exitKey] of this.teleportedPairs) {
            this.visitedCells.add(entryKey);
            this.visitedCells.add(exitKey);
            for (const k of [entryKey, exitKey]) {
                const pos = parsePositionKey(k);
                const digit = this.grid.getDigitAt(pos);
                if (digit !== null) this.digitsReached.add(digit);
            }
        }
    }

    // -- Click Handling with Teleport --------------------------------------

    override handleClick(pos: Position): boolean {
        if (!this.grid.isValidPosition(pos)) return false;

        const posKey = positionKey(pos);
        const selected = this.selectedCell;

        // Case 1: No cell selected - select
        if (!selected) {
            this.selectCell(pos);
            return true;
        }

        const selectedKey = positionKey(selected);

        // Case 2: Clicked selected cell
        if (posKey === selectedKey) {
            // If selected is a teleport exit, atomic backtrack: undo teleport + segment
            const teleportEntryKey = this.findTeleportEntryFor(selectedKey);
            if (teleportEntryKey) {
                this.teleportedPairs.delete(teleportEntryKey);
                const entryPos = parsePositionKey(teleportEntryKey);
                const segNeighbors = this.getSegmentNeighborsOf(entryPos);
                if (segNeighbors.length === 1) {
                    const fromPos = segNeighbors[0];
                    this.removeSegment(entryPos, fromPos);
                    this.selectedCell = fromPos;
                    if (this.getConnectedCells(fromPos).length > 0 || this.segments.size === 0) {
                        this.visitedCells.add(positionKey(fromPos));
                    }
                } else {
                    this.rebuildState();
                    this.selectedCell = entryPos;
                    this.visitedCells.add(teleportEntryKey);
                }
                return true;
            }
            this.deselectAndRemoveSegments();
            return true;
        }

        const connectedCells = this.getConnectedCells(selected);
        const isConnected = connectedCells.some(c => positionKey(c) === posKey);

        // Case 3: Backtracking (connected cell)
        if (isConnected) {
            // Check if this is a teleport backtrack: selected is teleport exit, clicked is entry gate
            const teleportEntryKey = this.findTeleportEntryFor(selectedKey);
            if (teleportEntryKey && teleportEntryKey === posKey) {
                // Atomic backtrack: undo teleport AND segment in one step
                this.teleportedPairs.delete(posKey);

                // Find the segment neighbor of the entry gate (the cell before it)
                const segNeighbors = this.getSegmentNeighborsOf(pos);
                if (segNeighbors.length === 1) {
                    const fromPos = segNeighbors[0];
                    this.removeSegment(pos, fromPos);
                    this.selectedCell = fromPos;
                    if (this.getConnectedCells(fromPos).length > 0 || this.segments.size === 0) {
                        this.visitedCells.add(positionKey(fromPos));
                    }
                } else {
                    // Edge case: entry gate has multiple segment connections
                    this.rebuildState();
                    this.selectedCell = pos;
                    this.visitedCells.add(posKey);
                }
                return true;
            }

            // Normal backtrack: remove segment
            this.removeSegment(selected, pos);
            this.selectedCell = pos;
            if (this.getConnectedCells(pos).length > 0 || this.segments.size === 0) {
                this.visitedCells.add(posKey);
            }
            return true;
        }

        // Case 4: Accessible cell - extend with potential teleport
        const accessible = this.getAccessibleCells();
        const isAccessible = accessible.some(c => positionKey(c) === posKey);

        if (isAccessible) {
            this.createSegment(selected, pos);
            this.selectedCell = pos;

            // Check if landed on an active gate -> teleport
            if (this.gm.raw.has(posKey)) {
                const pairedKey = this.gm.raw.get(posKey)!;
                if (!this.visitedCells.has(pairedKey)) {
                    const pairedPos = parsePositionKey(pairedKey);
                    this.teleportedPairs.set(posKey, pairedKey);
                    this.visitedCells.add(posKey);
                    this.visitedCells.add(pairedKey);
                    this.selectedCell = pairedPos;
                    const digit = this.grid.getDigitAt(pairedPos);
                    if (digit !== null) this.digitsReached.add(digit);
                }
            }

            return true;
        }

        // Case 5: Visited cell - switch focus
        if (this.visitedCells.has(posKey)) {
            // If clicking a teleport entry gate, select the exit cell instead
            if (this.teleportedPairs.has(posKey)) {
                const exitKey = this.teleportedPairs.get(posKey)!;
                this.selectedCell = parsePositionKey(exitKey);
                return true;
            }
            this.selectedCell = pos;
            return true;
        }

        // Case 6: New disconnected section
        this.selectCell(pos);
        return true;
    }

    override getAccessibleCells(): Position[] {
        const accessible = super.getAccessibleCells();

        if (!this.selectedCell) return accessible;

        const selectedKey = positionKey(this.selectedCell);

        // Add teleport entry gate as backtrack target (not a knight move away)
        const teleportEntryKey = this.findTeleportEntryFor(selectedKey);
        if (teleportEntryKey) {
            const entryPos = parsePositionKey(teleportEntryKey);
            if (!accessible.some(p => positionKey(p) === teleportEntryKey)) {
                accessible.push(entryPos);
            }
        }

        // Build set of connected cells so we don't filter out backtrack targets
        const connectedKeys = new Set(
            this.getConnectedCells(this.selectedCell).map(c => positionKey(c))
        );

        // Filter out active gate cells whose paired cell is already visited (forward moves only).
        // If the gate cell itself is already visited, we're joining to an existing endpoint,
        // not triggering a new teleport, so allow it.
        return accessible.filter(pos => {
            const key = positionKey(pos);
            if (connectedKeys.has(key)) return true; // backtrack targets always allowed
            if (this.gm.raw.has(key)) {
                const pairedKey = this.gm.raw.get(key)!;
                if (this.visitedCells.has(pairedKey) && !this.visitedCells.has(key)) {
                    return false;
                }
            }
            return true;
        });
    }

    override deselectAndRemoveSegments(): void {
        if (!this.selectedCell) return;
        const selectedKey = positionKey(this.selectedCell);

        // Remove teleport links involving the selected cell
        const teleportEntry = this.findTeleportEntryFor(selectedKey);
        if (teleportEntry) {
            this.teleportedPairs.delete(teleportEntry);
        }
        if (this.teleportedPairs.has(selectedKey)) {
            this.teleportedPairs.delete(selectedKey);
        }

        super.deselectAndRemoveSegments();
    }

    /**
     * Find the teleport entry key for a given exit key.
     */
    private findTeleportEntryFor(exitKey: string): string | null {
        for (const [entry, exit] of this.teleportedPairs) {
            if (exit === exitKey) return entry;
        }
        return null;
    }

    /**
     * Get cells connected to pos via segments only (not teleport links).
     */
    private getSegmentNeighborsOf(pos: Position): Position[] {
        const key = positionKey(pos);
        const neighbors: Position[] = [];
        for (const seg of this.segments) {
            const [a, b] = parseSegmentKey(seg);
            if (positionKey(a) === key) neighbors.push(b);
            else if (positionKey(b) === key) neighbors.push(a);
        }
        return neighbors;
    }

    // -- State Management --------------------------------------------------

    override reset(): void {
        super.reset();
        this.gm.clearActiveGates();
        this.teleportedPairs.clear();
    }

    override isStarted(): boolean {
        return super.isStarted() || this.gm.hasActiveGates();
    }

    // -- Serialization -----------------------------------------------------

    override serialize(): string {
        // Gate pairs only (compact). Teleport direction is recomputed on load — the
        // knight crosses any active gate its path reaches, automatically.
        return this.gm.serializeWithBase(super.serialize(), this.gs);
    }

    override deserialize(serialized: string): void {
        this.gm.clearActiveGates();
        this.teleportedPairs.clear();

        if (!serialized) {
            super.deserialize('');
            return;
        }

        const { basePart, gatePart } = VortexGateManager.splitSerialized(serialized);
        super.deserialize(basePart);
        this.gm.deserializeGates(gatePart, this.gs);
        this.recomputeTeleports();
        this.rebuildState();
    }

    /**
     * Recompute teleport links from the segments + active gates. Gate-crossing is
     * automatic: an active gate pair is crossed iff the path (a segment endpoint)
     * reaches either of its cells; the cell reached by a segment is the entry.
     */
    private recomputeTeleports(): void {
        const endpoints = new Set<string>();
        for (const seg of this.segments) {
            const [a, b] = parseSegmentKey(seg);
            endpoints.add(positionKey(a));
            endpoints.add(positionKey(b));
        }

        this.teleportedPairs.clear();
        const seen = new Set<string>();
        for (const [key, pairedKey] of this.gm.raw) {
            if (seen.has(key)) continue;
            seen.add(key);
            seen.add(pairedKey);

            const keyIn = endpoints.has(key);
            const pairIn = endpoints.has(pairedKey);
            if (keyIn || pairIn) {
                const entry = keyIn ? key : pairedKey;
                const exit = keyIn ? pairedKey : key;
                this.teleportedPairs.set(entry, exit);
            }
        }
    }
}
